import { z } from 'zod/v4';

import {
    animationSchemaLegacy,
    selectedFileSchemaLegacy,
    shadowSettingsSchemaLegacy,
} from '../../../widget/common/schemas/legacy';
import { legacyBag, legacyBoolean, legacyEnum, legacyJssValue, legacyNumber, legacyString } from '../../../legacy-leaf';

/**
 * JSS values are `string | number` and the host writes both (paddings and
 * widths as numbers, colours and border styles as strings), so the style bags
 * keep them as-is rather than coercing — these schemas describe what is
 * persisted, they do not reshape it. Every leaf here is a `legacy-leaf`: a value
 * of the wrong type reads as absent instead of failing the column.
 */
const jssValueSchemaLegacy = legacyJssValue();

/**
 * The picked background file, plus the resize flag the column adds on top of the
 * shared file shape.
 */
export const columnBackgroundImageSchemaLegacy = legacyBag(
    selectedFileSchemaLegacy.extend({ hasResize: legacyBoolean() })
);

/**
 * Desktop settings. `verticalAlign`, `horizontalAlign` and `animation` are
 * nullable because the header (`specific`) columns are seeded without them:
 * those three are laid out by the row, not the column.
 */
export const columnDataSchemaLegacy = z.object({
    columnWidth: legacyNumber(),
    columnWidthPer: legacyNumber(),

    verticalAlign: legacyEnum(['T', 'M', 'B']),
    horizontalAlign: legacyEnum(['L', 'M', 'R']),

    isShadow: legacyBoolean(),
    shadowSettings: legacyBag(shadowSettingsSchemaLegacy),

    isBorder: legacyBoolean(),
    isIndependentBorders: legacyBoolean(),
    isIndependentCorners: legacyBoolean(),
    isSynchronizeStyle: legacyBoolean(),

    animation: legacyBag(animationSchemaLegacy),

    backgroundImage: columnBackgroundImageSchemaLegacy,

    isColorOverflow: legacyBoolean(),
    colorOverflow: legacyString(),
    colorOverflowOpacity: legacyNumber(),

    isScrollEffect: legacyBoolean(),
    /** `EScrollEffect` */
    scrollEffect: legacyString(),

    isFilterOverlay: legacyBoolean(),
    /** `EFilerOverlay` — a CSS filter literal rather than a symbolic name. */
    filterOverlay: legacyString(),
});

/**
 * Per-device data override, shared by mobile and tablet. Every key is optional:
 * the host only writes the ones the user touched.
 */
export const columnDeviceDataSchemaLegacy = z.object({
    backgroundImage: columnBackgroundImageSchemaLegacy,

    isBorder: legacyBoolean(),
    isIndependentBorders: legacyBoolean(),
    isIndependentCorners: legacyBoolean(),

    isColorOverflow: legacyBoolean(),
    colorOverflow: legacyString(),
    colorOverflowOpacity: legacyNumber(),

    isFilterOverlay: legacyBoolean(),
    filterOverlay: legacyString(),
});

/** Desktop styles. These land in a `Pick<JssStyle, …>` on the host, so every key stays loose. */
export const columnStylesSchemaLegacy = z.object({
    backgroundColor: jssValueSchemaLegacy,

    paddingTop: jssValueSchemaLegacy,
    paddingBottom: jssValueSchemaLegacy,
    paddingLeft: jssValueSchemaLegacy,
    paddingRight: jssValueSchemaLegacy,

    borderRadius: jssValueSchemaLegacy,
    borderTopLeftRadius: jssValueSchemaLegacy,
    borderTopRightRadius: jssValueSchemaLegacy,
    borderBottomRightRadius: jssValueSchemaLegacy,
    borderBottomLeftRadius: jssValueSchemaLegacy,

    borderWidth: jssValueSchemaLegacy,
    borderStyle: jssValueSchemaLegacy,
    borderColor: jssValueSchemaLegacy,

    borderTopWidth: jssValueSchemaLegacy,
    borderTopStyle: jssValueSchemaLegacy,
    borderTopColor: jssValueSchemaLegacy,
    borderRightWidth: jssValueSchemaLegacy,
    borderRightStyle: jssValueSchemaLegacy,
    borderRightColor: jssValueSchemaLegacy,
    borderBottomWidth: jssValueSchemaLegacy,
    borderBottomStyle: jssValueSchemaLegacy,
    borderBottomColor: jssValueSchemaLegacy,
    borderLeftWidth: jssValueSchemaLegacy,
    borderLeftStyle: jssValueSchemaLegacy,
    borderLeftColor: jssValueSchemaLegacy,

    backgroundSize: jssValueSchemaLegacy,
    backgroundPosition: jssValueSchemaLegacy,
    backgroundRepeat: jssValueSchemaLegacy,
});

/**
 * Per-device style override — the desktop bag minus the shorthand border trio,
 * which mobile and tablet never carry.
 */
export const columnDeviceStylesSchemaLegacy = columnStylesSchemaLegacy.omit({
    borderWidth: true,
    borderStyle: true,
    borderColor: true,
});

export type ColumnDataLegacy = z.infer<typeof columnDataSchemaLegacy>;
export type ColumnDeviceDataLegacy = z.infer<typeof columnDeviceDataSchemaLegacy>;
export type ColumnStylesLegacy = z.infer<typeof columnStylesSchemaLegacy>;
export type ColumnDeviceStylesLegacy = z.infer<typeof columnDeviceStylesSchemaLegacy>;
